<script setup>
import Grid from "../../../../frontend/src/components/Grid.vue";
import {onMounted, ref, watch} from "vue";
import useGrid from "@/composables/useGrid";
import crawlEcosystemsService from "@/services/crawl-ecosystems.service";
import crawlEcosystemsStartupsService from "@/services/crawl-ecosystems-startups.service";
import VueMultiselect from "vue-multiselect";

const {items, gridParams, gridMethods} = useGrid(crawlEcosystemsService.getList);
const mainDomain = process.env.VUE_APP_MAIN_DOMAIN;

const addFilterOptions = [
    {id: 'all', name: 'All'},
    {id: 'linked', name: 'Only linked'},
    {id: 'no_bot', name: 'Ecosystems with no bot'},
    {id: 'no_ecosystem', name: 'Bots with no ecosystem'},
];

const columns = [
    {
        label: 'Bot ID',
        name: 'bot_id',
        filter: 'text',
        sortable: true,
    },
    {
        label: 'Ecosystem ID',
        name: 'ecosystem_id',
        component: 'RawColumn',
        filter: 'text',
        sortable: true,
    },
    {
        label: 'Ecosystem Name',
        name: 'ecosystem_name',
        filter: 'text',
    },
    {
        label: 'Web source',
        name: 'web_source',
        filter: 'text',
    },
    {
        label: 'Last Run Done',
        name: 'last_run_done',
        component: 'Date',
        sortable: true,
    },
    {
        label: 'Approve Status',
        name: 'approve_status',
        filter: 'single-select',
        options: [
            {
                id: 'in_review',
                name: 'in_review'
            },
            {
                id: 'approved',
                name: 'approved'
            },
            {
                id: 'rejected',
                name: 'rejected'
            },
            {
                id: 'private',
                name: 'private'
            }
        ],
    },
    {
        label: 'Not Linked Startups',
        name: 'not_linked_startups_count',
        component: 'Link',
        sortable: true,
    },
    {
        label: 'Linked Startups',
        name: 'linked_startups_count',
        component: 'Link',
        sortable: true,
    },
    {
        label: 'CB Startups',
        name: 'cb_startups_count',
        component: 'Link',
        sortable: true,
    },
];

const showNotLinkedStartupsModal = ref(false);
const notLinkedStartups = ref([]);

const showLinkedStartupsModal = ref(false);
const linkedStartups = ref([]);

const showCbStartupsModal = ref(false);
const cbStartups = ref([]);

const addFilter = ref('');

watch(addFilter, (value) => {
    gridMethods.setFilters({
        ...gridParams.filters,
        add: value.id,
    });
});

onMounted(() => {
    gridMethods.setColumns(columns);
    gridMethods.resetList();
});

function gridAction(params) {
    if (params.action === 'showNotLinkedStartups') {
        showNotLinkedStartups(params.rowId);
    } else if (params.action === 'showLinkedStartups') {
        showLinkedStartups(params.rowId);
    } else if (params.action === 'showCbStartups') {
        showCbStartups(params.params.ecosystem_id);
    }
}

async function showNotLinkedStartups(botId) {
    const {data} = await crawlEcosystemsStartupsService.getNotLinkedList(botId);

    notLinkedStartups.value = data;
    showNotLinkedStartupsModal.value = true;
}

async function showLinkedStartups(botId) {
    const {data} = await crawlEcosystemsStartupsService.getLinkedList(botId);

    linkedStartups.value = data;
    showLinkedStartupsModal.value = true;
}

async function showCbStartups(ecosystemId) {
    const {data} = await crawlEcosystemsStartupsService.getCbList(ecosystemId);

    linkedStartups.value = data;
    showLinkedStartupsModal.value = true;
}

</script>

<template>
    <div>
        <div class="topbar">
            <div style="width: 300px; display: flex; align-items: center; gap: 10px;">
                <div>
                    Filter:
                </div>

                <VueMultiselect
                    v-model="addFilter"
                    :options="addFilterOptions"
                    select-label=""
                    deselect-label=""
                    label="name"
                    track-by="name"
                    class="multiselect-mid pl-2"
                />
            </div>

            <div class="mt-2">
                Total rows: {{ gridParams.totalRowsCount }}
            </div>
        </div>

        <div class="main-table-wrapper">
            <div class="main-table overflow-auto">
                <Grid
                    :columns="columns"
                    :data="items"
                    has-filters
                    :page-length="parseInt(gridParams.pageLength)"
                    :total-rows-count="gridParams.totalRowsCount"
                    :sort="gridParams.sort"
                    :currentPage="gridParams.currentPage"
                    @changePage="gridMethods.changePage"
                    @changeFilters="gridMethods.setFilters"
                    @changeSort="gridMethods.changeSort"
                    @action="gridAction"
                />
            </div>
        </div>

        <b-modal v-model="showNotLinkedStartupsModal" modal-class="modal-w-lg" title="Not Linked Startups" hide-footer>
            <table v-if="notLinkedStartups.length" class="table">
                <thead>
                    <tr>
                        <th>Startup Name</th>
                        <th>Startup URL</th>
                        <th>Sync Details</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(startup, index) in notLinkedStartups" :key="index">
                        <td>{{ startup.name }}</td>
                        <td>
                            <a :href="startup.url" target="_blank">{{ startup.url }}</a>
                        </td>
                        <td>
                            {{startup.sync_details}}
                        </td>
                    </tr>
                </tbody>
            </table>
        </b-modal>

        <b-modal v-model="showLinkedStartupsModal" modal-class="modal-w-xxl" title="Linked Startups" hide-footer>
            <table v-if="linkedStartups.length" class="table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>URL Domain</th>
                        <th>Short Description</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(startup, index) in linkedStartups" :key="index">
                        <td><a :href="`${mainDomain}/startups/${startup.id}`" target="_blank">{{ startup.id }}</a></td>
                        <td>{{ startup.name }}</td>
                        <td>{{ startup.url_domain }}</td>
                        <td>{{ startup.short_description }}</td>
                    </tr>
                </tbody>
            </table>
        </b-modal>

        <b-modal v-model="showCbStartupsModal" modal-class="modal-w-xxl" title="Linked Startups" hide-footer>
            <table v-if="cbStartups.length" class="table">
                <thead>
                <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>URL Domain</th>
                    <th>Short Description</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(startup, index) in cbStartups" :key="index">
                    <td><a :href="`${mainDomain}/startups/${startup.id}`" target="_blank">{{ startup.id }}</a></td>
                    <td>{{ startup.name }}</td>
                    <td>{{ startup.url_domain }}</td>
                    <td>{{ startup.short_description }}</td>
                </tr>
                </tbody>
            </table>
        </b-modal>
    </div>
</template>

<style scoped>

</style>
